const chapter = {
  id: 'matrices',
  slug: 'matrices',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Matrices',
        hindi: 'आव्यूह',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Easy–Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main',
        hindi: 'जेईई मेन में 1-2 प्रश्न',
  },
  syllabusTags: ['Algebra', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'intro',
      title: {
        english: 'Matrix: Definition & Types',
        hindi: 'आव्यूह: परिभाषा और प्रकार',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Matrix',
        hindi: 'आव्यूह',
          },
          body: {
            english: 'A rectangular array of numbers arranged in $m$ rows and $n$ columns is an $m \\times n$ matrix, denoted $A = [a_{ij}]_{m \\times n}$. Element $a_{ij}$ lies in row $i$, column $j$.',
        hindi: '$m$ पंक्तियों और $n$ कॉलमों में व्यवस्थित संख्याओं की एक आयताकार सरणी एक $m \\times n$ आव्यूह है, जिसे $A = [a_{ij}]_{m \\times n}$ दर्शाया गया है। तत्व $a_{ij}$ पंक्ति $i$, कॉलम $j$ में स्थित है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Row matrix**: $1 \\times n$. **Column matrix**: $m \\times 1$.',
        hindi: '**पंक्ति आव्यूह**: $1 \\times n$। **कॉलम आव्यूह**: $m \\times 1$.',
            },
            {
              english: '**Square matrix**: $m = n$ (order $n$).',
        hindi: '**स्क्वायर आव्यूह**: $m = n$ (आदेश $n$)।',
            },
            {
              english: '**Diagonal matrix**: square, $a_{ij} = 0$ for $i \\ne j$.',
        hindi: '**विकर्ण आव्यूह**: वर्ग, $i \\ne j$ के लिए $a_{ij} = 0$।',
            },
            {
              english: '**Scalar matrix**: diagonal with all diagonal entries equal.',
        hindi: '**स्केलर आव्यूह**: सभी विकर्ण प्रविष्टियों के साथ विकर्ण समान।',
            },
            {
              english: '**Identity** $I_n$: scalar matrix with diagonal entries $= 1$.',
        hindi: '**पहचान** $I_n$: विकर्ण प्रविष्टियों के साथ अदिश आव्यूह $= 1$।',
            },
            {
              english: '**Zero/null matrix** $O$: all entries $0$.',
        hindi: '**Zero/null आव्यूह** $O$: सभी प्रविष्टियाँ $0$।',
            },
            {
              english: '**Upper/Lower triangular**: $a_{ij} = 0$ when $i > j$ (upper) or $i < j$ (lower).',
        hindi: '**ऊपरी/निचला त्रिकोणीय**: $a_{ij} = 0$ जब $i > j$ (ऊपरी) या $i < j$ (निचला)।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Two matrices are **equal** iff they have the same order *and* corresponding entries are equal.',
        hindi: 'दो आव्यूह **समान** हैं यदि उनका क्रम समान है *और* संगत प्रविष्टियाँ समान हैं।',
          },
        },
      ],
    },
    {
      id: 'operations',
      title: {
        english: 'Algebra of Matrices',
        hindi: 'आव्यूहों का बीजगणित',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Addition**: same order, $(A + B)_{ij} = a_{ij} + b_{ij}$. Commutative & associative.',
        hindi: '**जोड़**: समान क्रम, $(A + B)_{ij} = a_{ij} + b_{ij}$। क्रमविनिमेय एवं साहचर्य.',
            },
            {
              english: '**Scalar multiplication**: $(kA)_{ij} = k a_{ij}$.',
        hindi: '**अदिश गुणन**: $(kA)_{ij} = k a_{ij}$.',
            },
            {
              english: '**Multiplication** $AB$: defined iff cols$(A) = $ rows$(B)$. $(AB)_{ij} = \\sum_k a_{ik} b_{kj}$.',
        hindi: '**गुणन** $AB$: परिभाषित यदि iff cols$(A) = $ पंक्तियाँ$(B)$। $(AB)_{ij} = \\sum_k a_{ik} b_{kj}$.',
            },
            {
              english: 'Matrix multiplication is **associative** and **distributive** over $+$, but **not commutative** in general: $AB \\ne BA$.',
        hindi: 'आव्यूह गुणन $+$ से अधिक **साहचर्य** और **वितरणात्मक** है, लेकिन सामान्य तौर पर **क्रमविनिमेय नहीं** है: $AB \\ne BA$।',
            },
            {
              english: 'If $AB = BA$, $A$ and $B$ are said to **commute**.',
        hindi: 'यदि $AB = BA$, $A$ और $B$ को **यात्रा** कहा जाता है।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '$AB = O$ does **not** imply $A = O$ or $B = O$. Also $AB = AC$ does not imply $B = C$ (cancellation fails).',
        hindi: '$AB = O$ का तात्पर्य $A = O$ या $B = O$ **नहीं** है। साथ ही $AB = AC$ का अर्थ $B = C$ नहीं है (रद्दीकरण विफल रहता है)।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'If $A = \\begin{pmatrix} 1 & 2 \\\\ 3 & 4 \\end{pmatrix}$, find $A^2$.',
        hindi: 'यदि $A = \\begin{pmatrix} 1 & 2 \\\\ 3 & 4 \\end{pmatrix}$, तो $A^2$ ज्ञात कीजिए।',
          },
          solution: {
            english: '$A^2 = \\begin{pmatrix} 1\\cdot1+2\\cdot3 & 1\\cdot2+2\\cdot4 \\\\ 3\\cdot1+4\\cdot3 & 3\\cdot2+4\\cdot4 \\end{pmatrix} = \\begin{pmatrix} 7 & 10 \\\\ 15 & 22 \\end{pmatrix}$.',
        hindi: '$A^2 = \\begin{pmatrix} 1\\cdot1+2\\cdot3 & 1\\cdot2+2\\cdot4 \\\\ 3\\cdot1+4\\cdot3 & 3\\cdot2+4\\cdot4 \\end{pmatrix} = \\begin{pmatrix} 7 & 10 \\\\ 15 & 22 \\end{pmatrix}$.',
          },
        },
      ],
    },
    {
      id: 'transpose',
      title: {
        english: 'Transpose, Symmetric & Skew-Symmetric',
        hindi: 'परिवर्त, सममित और विषम सममित',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Transpose',
        hindi: 'स्थानांतरण',
          },
          body: {
            english: '$A^T$ (or $A\'$) is obtained by interchanging rows and columns: $(A^T)_{ij} = a_{ji}$.',
        hindi: '$A^T$ (या $A\\\'$) पंक्तियों और स्तंभों को आपस में बदलकर प्राप्त किया जाता है: $(A^T)_{ij} = a_{ji}$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$(A^T)^T = A$',
        hindi: '$(A^T)^T = A$',
            },
            {
              english: '$(A + B)^T = A^T + B^T$',
        hindi: '$(A + B)^T = A^T + B^T$',
            },
            {
              english: '$(kA)^T = k A^T$',
        hindi: '$(kA)^T = k A^T$',
            },
            {
              english: '$(AB)^T = B^T A^T$ — **reverse order**.',
        hindi: '$(AB)^T = B^T A^T$ - **रिवर्स ऑर्डर**।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Symmetric / Skew-symmetric',
        hindi: 'सममित/तिरछा-सममित',
          },
          body: {
            english: '$A$ is **symmetric** if $A^T = A$ (so $a_{ij} = a_{ji}$). $A$ is **skew-symmetric** if $A^T = -A$ (so $a_{ji} = -a_{ij}$, forcing diagonal entries $= 0$).',
        hindi: '$A$ **सममित** है यदि $A^T = A$ (इसलिए $a_{ij} = a_{ji}$)। $A$ **तिरछा-सममित** है यदि $A^T = -A$ (तो $a_{ji} = -a_{ij}$, विकर्ण प्रविष्टियों को बाध्य करता है $= 0$)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Decomposition',
        hindi: 'अपघटन',
          },
          statement: {
            english: 'Every square matrix $A$ can be written uniquely as $A = P + Q$ where $P = \\tfrac{1}{2}(A + A^T)$ is symmetric and $Q = \\tfrac{1}{2}(A - A^T)$ is skew-symmetric.',
        hindi: 'प्रत्येक वर्ग आव्यूह $A$ को विशिष्ट रूप से $A = P + Q$ के रूप में लिखा जा सकता है, जहां $P = \\tfrac{1}{2}(A + A^T)$ सममित है और $Q = \\tfrac{1}{2}(A - A^T)$ तिरछा-सममित है।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Express $A = \\begin{pmatrix} 2 & 4 \\\\ -2 & 6 \\end{pmatrix}$ as a sum of symmetric and skew-symmetric matrices.',
        hindi: '$A = \\begin{pmatrix} 2 & 4 \\\\\\\\ -2 & 6 \\end{pmatrix}$ को सममित और तिरछा-सममित आव्यूह के योग के रूप में व्यक्त करें।',
          },
          solution: {
            english: '$A^T = \\begin{pmatrix} 2 & -2 \\\\ 4 & 6 \\end{pmatrix}$. $P = \\tfrac{1}{2}(A+A^T) = \\begin{pmatrix} 2 & 1 \\\\ 1 & 6 \\end{pmatrix}$, $Q = \\tfrac{1}{2}(A-A^T) = \\begin{pmatrix} 0 & 3 \\\\ -3 & 0 \\end{pmatrix}$.',
        hindi: '$A^T = \\begin{pmatrix} 2 & -2 \\\\ 4 & 6 \\end{pmatrix}$. $P = \\tfrac{1}{2}(A+A^T) = \\begin{pmatrix} 2 & 1 \\\\ 1 & 6 \\end{pmatrix}$, $Q = \\tfrac{1}{2}(A-A^T) = \\begin{pmatrix} 0 & 3 \\\\ -3 & 0 \\end{pmatrix}$.',
          },
        },
      ],
    },
    {
      id: 'special',
      title: {
        english: 'Special Matrices: Orthogonal, Idempotent, Nilpotent, Involutory',
        hindi: 'विशेष आव्यूह: लम्बकोणीय, समधातीय, शून्यघातीय, अनुविलोम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Orthogonal**: $A A^T = A^T A = I$ (so $A^{-1} = A^T$).',
        hindi: '**ऑर्थोगोनल**: $A A^T = A^T A = I$ (इसलिए $A^{-1} = A^T$)।',
            },
            {
              english: '**Idempotent**: $A^2 = A$.',
        hindi: '**निष्क्रिय**: $A^2 = A$।',
            },
            {
              english: '**Nilpotent of index $k$**: $A^k = O$ but $A^{k-1} \\ne O$.',
        hindi: '**सूचकांक का निलपोटेंट $k$**: $A^k = O$ लेकिन $A^{k-1} \\ne O$।',
            },
            {
              english: '**Involutory**: $A^2 = I$ (so $A^{-1} = A$).',
        hindi: '**अनैच्छिक**: $A^2 = I$ (इसलिए $A^{-1} = A$)। **हर्मिटियन**: $A^2 = I$ (conjugate-transpose); वास्तविक आव्यूहों के लिए सममिति के समान।',
            },
            {
              english: '**Hermitian**: $A^\\theta = A$ (conjugate-transpose); for real matrices same as symmetric.',
        hindi: '**हर्मिटियन**: $A^\\theta = A$ (conjugate-transpose); वास्तविक आव्यूहों के लिए सममिति के समान।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'For an idempotent matrix, $\\det(A) \\in \\{0, 1\\}$. For nilpotent, $\\det(A) = 0$. For involutory, $\\det(A) = \\pm 1$.',
        hindi: 'एक निष्क्रिय आव्यूह के लिए, $\\det(A) \\in \\{0, 1\\}$। निलपोटेंट के लिए, $\\det(A) = 0$। अनैच्छिक के लिए, $\\det(A) = \\pm 1$।',
          },
        },
      ],
    },
    {
      id: 'inverse',
      title: {
        english: 'Inverse via Adjoint',
        hindi: 'सहायक द्वारा व्युत्क्रम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Adjoint',
        hindi: 'जोड़',
          },
          body: {
            english: 'For square $A$ of order $n$, $\\text{adj}(A)$ is the transpose of the cofactor matrix: $[\\text{adj}\\,A]_{ij} = C_{ji}$, where $C_{ij} = (-1)^{i+j} M_{ij}$ and $M_{ij}$ is the $(i,j)$ minor (determinant after deleting row $i$, column $j$).',
        hindi: '$n$ क्रम के वर्ग $A$ के लिए, $\\text{adj}(A)$ सहकारक मैट्रिक्स का स्थानान्तरण है: $[\\text{adj}\\,A]_{ij} = C_{ji}$, जहां $C_{ij} = (-1)^{i+j} M_{ij}$ और $M_{ij}$ $(i,j)$ लघु (पंक्ति $i$, स्तंभ $j$ को हटाने के बाद निर्धारक) है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Inverse formula',
        hindi: 'उलटा सूत्र',
          },
          statement: {
            english: 'If $\\det(A) \\ne 0$, then $A^{-1} = \\dfrac{1}{\\det(A)}\\,\\text{adj}(A)$. Such $A$ is called **non-singular** or **invertible**.',
        hindi: 'यदि $\\det(A) \\ne 0$, तो $A^{-1} = \\dfrac{1}{\\det(A)}\\,\\text{adj}(A)$. ऐसे $A$ को **गैर-एकवचन** या **उलटा** कहा जाता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$A \\cdot \\text{adj}(A) = \\text{adj}(A) \\cdot A = \\det(A)\\,I$',
        hindi: '$A \\cdot \\text{adj}(A) = \\text{adj}(A) \\cdot A = \\det(A)\\,I$',
            },
            {
              english: '$\\det(\\text{adj}\\,A) = (\\det A)^{n-1}$',
        hindi: '$\\det(\\text{adj}\\,A) = (\\det A)^{n-1}$',
            },
            {
              english: '$\\text{adj}(\\text{adj}\\,A) = (\\det A)^{n-2} A$',
        hindi: '$\\text{adj}(\\text{adj}\\,A) = (\\det A)^{n-2} A$',
            },
            {
              english: '$(AB)^{-1} = B^{-1} A^{-1}$ — reverse order',
        hindi: '$(AB)^{-1} = B^{-1} A^{-1}$ — विपरीत क्रम',
            },
            {
              english: '$(A^{-1})^T = (A^T)^{-1}$',
        hindi: '$(A^{-1})^T = (A^T)^{-1}$',
            },
          ],
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find $A^{-1}$ for $A = \\begin{pmatrix} 2 & 3 \\\\ 1 & 4 \\end{pmatrix}$.',
        hindi: '$A = \\begin{pmatrix} 2 & 3 \\\\ 1 & 4 \\end{pmatrix}$ के लिए $A^{-1}$ खोजें।',
          },
          solution: {
            english: '$\\det(A) = 8 - 3 = 5$. $\\text{adj}(A) = \\begin{pmatrix} 4 & -3 \\\\ -1 & 2 \\end{pmatrix}$. So $A^{-1} = \\tfrac{1}{5}\\begin{pmatrix} 4 & -3 \\\\ -1 & 2 \\end{pmatrix}$.',
        hindi: '$\\det(A) = 8 - 3 = 5$. $\\text{adj}(A) = \\begin{pmatrix} 4 & -3 \\\\ -1 & 2 \\end{pmatrix}$. तो $A^{-1} = \\tfrac{1}{5}\\begin{pmatrix} 4 & -3 \\\\ -1 & 2 \\end{pmatrix}$.',
          },
        },
      ],
    },
    {
      id: 'elementary',
      title: {
        english: 'Elementary Operations & Inverse by Row Reduction',
        hindi: 'प्राथमिक संक्रियाएँ और पंक्ति अवकरण द्वारा व्युत्क्रम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$R_i \\leftrightarrow R_j$: swap rows.',
        hindi: '$R_i \\leftrightarrow R_j$: पंक्तियों की अदला-बदली करें।',
            },
            {
              english: '$R_i \\to k R_i$ ($k \\ne 0$): scale a row.',
        hindi: '$R_i \\to k R_i$ ($k \\ne 0$): एक पंक्ति को स्केल करें।',
            },
            {
              english: '$R_i \\to R_i + k R_j$: add multiple of one row to another.',
        hindi: '$R_i \\to R_i + k R_j$: एक पंक्ति के एकाधिक को दूसरी पंक्ति में जोड़ें।',
            },
          ],
        },
        {
          type: 'paragraph',
          text: {
            english: 'To find $A^{-1}$ by row reduction, write $[A \\mid I]$ and reduce $A$ to $I$ via row operations. The right block becomes $A^{-1}$. Column operations work analogously, with $[A \\mid I]^T$.',
        hindi: 'पंक्ति कटौती द्वारा $A^{-1}$ खोजने के लिए, $[A \\mid I]$ लिखें और पंक्ति संचालन के माध्यम से $A$ को घटाकर $I$ करें। दायां ब्लॉक $A^{-1}$ बन जाता है। कॉलम संचालन $[A \\mid I]^T$ के साथ समान रूप से काम करते हैं।',
          },
        },
      ],
    },
    {
      id: 'systems',
      title: {
        english: 'Systems of Linear Equations',
        hindi: 'रैखिक समीकरण निकाय',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A system $a_{i1} x_1 + \\cdots + a_{in} x_n = b_i$ ($i = 1,\\ldots,m$) writes compactly as $A X = B$ with $A$ the coefficient matrix, $X$ the column of unknowns, $B$ the constants column.',
        hindi: 'एक सिस्टम $a_{i1} x_1 + \\cdots + a_{in} x_n = b_i$ ($i = 1,\\ldots,m$) $A$ गुणांक मैट्रिक्स, $X$ अज्ञात के कॉलम, $B$ स्थिरांक कॉलम के साथ $A X = B$ के रूप में कॉम्पैक्ट रूप से लिखता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Matrix method (square, non-singular)',
        hindi: 'आव्यूह विधि (वर्ग, गैर-एकवचन)',
          },
          statement: {
            english: 'If $A$ is square and $\\det(A) \\ne 0$, the system $AX = B$ has the unique solution $X = A^{-1} B$.',
        hindi: 'यदि $A$ वर्गाकार है और $\\det(A) \\ne 0$ है, तो सिस्टम $AX = B$ का अद्वितीय समाधान $X = A^{-1} B$ है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Consistent**: at least one solution.',
        hindi: '**सुसंगत**: कम से कम एक समाधान। **असंगत**: कोई समाधान नहीं।',
            },
            {
              english: '**Inconsistent**: no solution.',
        hindi: '**असंगत**: कोई समाधान नहीं।',
            },
            {
              english: 'If $\\det(A) = 0$ and $(\\text{adj}\\,A) B = O$ — infinitely many solutions (consistent).',
        hindi: 'यदि $\\det(A) = 0$ और $(\\text{adj}\\,A) B = O$ - अपरिमित रूप से कई समाधान (सुसंगत)।',
            },
            {
              english: 'If $\\det(A) = 0$ and $(\\text{adj}\\,A) B \\ne O$ — no solution (inconsistent).',
        hindi: 'यदि $\\det(A) = 0$ और $(\\text{adj}\\,A) B \\ne O$ - कोई समाधान नहीं (असंगत)। **सजातीय** ($\\det(A) = 0$): तुच्छ समाधान $(\\text{adj}\\,A) B \\ne O$ हमेशा मौजूद रहता है; गैर-तुच्छ समाधान मौजूद हैं यदि ।',
            },
            {
              english: '**Homogeneous** ($B = O$): trivial solution $X = O$ always exists; non-trivial solutions exist iff $\\det(A) = 0$.',
        hindi: '**सजातीय** ($B = O$): तुच्छ समाधान $X = O$ हमेशा मौजूद रहता है; गैर-तुच्छ समाधान मौजूद हैं यदि $\\det(A) = 0$।',
            },
          ],
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Solve $2x + y = 5,\\ 3x + 2y = 8$ using matrix method.',
        hindi: 'आव्यूह विधि का उपयोग करके $2x + y = 5,\\\\ 3x + 2y = 8$ को हल करें।',
          },
          solution: {
            english: '$A = \\begin{pmatrix} 2 & 1 \\\\ 3 & 2 \\end{pmatrix}$, $\\det(A) = 1$. $A^{-1} = \\begin{pmatrix} 2 & -1 \\\\ -3 & 2 \\end{pmatrix}$. $X = A^{-1}\\begin{pmatrix} 5 \\\\ 8 \\end{pmatrix} = \\begin{pmatrix} 2 \\\\ 1 \\end{pmatrix}$. So $x = 2, y = 1$.',
        hindi: '$A = \\begin{pmatrix} 2 & 1 \\\\ 3 & 2 \\end{pmatrix}$, $\\det(A) = 1$. $A^{-1} = \\begin{pmatrix} 2 & -1 \\\\ -3 & 2 \\end{pmatrix}$. $X = A^{-1}\\begin{pmatrix} 5 \\\\ 8 \\end{pmatrix} = \\begin{pmatrix} 2 \\\\ 1 \\end{pmatrix}$. तो $x = 2, y = 1$.',
          },
        },
      ],
    },
    {
      id: 'cayley',
      title: {
        english: 'Cayley–Hamilton & Trace',
        hindi: 'केले-हैमिल्टन और अनुरेख',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Trace',
        hindi: 'ट्रेस',
          },
          body: {
            english: '$\\text{tr}(A) = \\sum_{i=1}^n a_{ii}$ (sum of diagonal entries). Properties: $\\text{tr}(A + B) = \\text{tr}(A) + \\text{tr}(B)$, $\\text{tr}(AB) = \\text{tr}(BA)$.',
        hindi: '$\\text{tr}(A) = \\sum_{i=1}^n a_{ii}$ (विकर्ण प्रविष्टियों का योग)। गुण: $\\text{tr}(A + B) = \\text{tr}(A) + \\text{tr}(B)$, $\\text{tr}(AB) = \\text{tr}(BA)$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Cayley–Hamilton',
        hindi: 'केली-हैमिल्टन',
          },
          statement: {
            english: `Every square matrix satisfies its own characteristic equation. For $2 \\times 2$ matrix $A$:
$$A^2 - \\text{tr}(A)\\,A + \\det(A)\\,I = O.$$
This gives $A^{-1} = \\dfrac{1}{\\det A}\\,[\\text{tr}(A) I - A]$ when $\\det A \\ne 0$.`,
            hindi: 'प्रत्येक वर्ग आव्यूह अपने स्वयं के विशिष्ट समीकरण को संतुष्ट करता है। $2 \\times 2$ आव्यूह $A$ के लिए: $$A^2 - \\text{tr}(A)\\,A + \\det(A)\\,I = O.$$ यह $A^{-1} = \\dfrac{1}{\\det A}\\,[\\text{tr}(A) I - A]$ देता है जब $\\det A \\ne 0$ होता है।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'For $A = \\begin{pmatrix} 1 & 2 \\\\ 3 & 4 \\end{pmatrix}$, verify Cayley–Hamilton and find $A^{-1}$.',
        hindi: '$A = \\begin{pmatrix} 1 & 2 \\\\\\\\ 3 & 4 \\end{pmatrix}$ के लिए, केली-हैमिल्टन को सत्यापित करें और $A^{-1}$ ढूंढें।',
          },
          solution: {
            english: '$\\text{tr}(A) = 5$, $\\det(A) = -2$. CH: $A^2 - 5A - 2I = O$. Then $A^{-1} = \\tfrac{1}{-2}(5I - A) = \\tfrac{1}{-2}\\begin{pmatrix} 4 & -2 \\\\ -3 & 1 \\end{pmatrix} = \\begin{pmatrix} -2 & 1 \\\\ 3/2 & -1/2 \\end{pmatrix}$.',
        hindi: '$\\text{tr}(A) = 5$, $\\det(A) = -2$. CH: $A^2 - 5A - 2I = O$. फिर $A^{-1} = \\tfrac{1}{-2}(5I - A) = \\tfrac{1}{-2}\\begin{pmatrix} 4 & -2 \\\\ -3 & 1 \\end{pmatrix} = \\begin{pmatrix} -2 & 1 \\\\ 3/2 & -1/2 \\end{pmatrix}$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '(AB)^T = B^T A^T',
      desc: {
        english: 'Transpose of product',
        hindi: 'उत्पाद का स्थानांतरण',
      },
    },
    {
      latex: '(AB)^{-1} = B^{-1} A^{-1}',
      desc: {
        english: 'Inverse of product',
        hindi: 'उत्पाद का उलटा',
      },
    },
    {
      latex: 'A^{-1} = \\tfrac{1}{\\det A}\\,\\text{adj}(A)',
      desc: {
        english: 'Inverse via adjoint',
        hindi: 'जोड़ के माध्यम से उलटा',
      },
    },
    {
      latex: 'A \\cdot \\text{adj}(A) = \\det(A)\\,I',
      desc: {
        english: 'Adjoint identity',
        hindi: 'संयुक्त पहचान',
      },
    },
    {
      latex: '\\det(\\text{adj}\\,A) = (\\det A)^{n-1}',
      desc: {
        english: '',
        hindi: '(adj\\,A) = ( A) n-1',
      },
    },
    {
      latex: 'A = \\tfrac{1}{2}(A+A^T) + \\tfrac{1}{2}(A-A^T)',
      desc: {
        english: 'Symm + skew decomposition',
        hindi: 'सिम्म + तिरछा अपघटन',
      },
    },
    {
      latex: 'A^2 - \\text{tr}(A)A + \\det(A) I = O',
      desc: {
        english: 'Cayley–Hamilton (2×2)',
        hindi: 'केली-हैमिल्टन (2×2)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Inverse',
      problem: {
        english: 'If $A = \\begin{pmatrix} 1 & 2 \\\\ 3 & 5 \\end{pmatrix}$, find $A^{-1}$.',
        hindi: 'यदि $A = \\begin{pmatrix} 1 & 2 \\\\ 3 & 5 \\end{pmatrix}$, तो $A^{-1}$ ज्ञात कीजिए।',
      },
      answer: {
        english: '$\\begin{pmatrix} -5 & 2 \\\\ 3 & -1 \\end{pmatrix}$',
        hindi: '$\\begin{pmatrix} -5 और 2 \\\\ 3 और -1 \\end{pmatrix}$',
      },
      solution: {
        english: '$\\det = -1$, adj $= \\begin{pmatrix} 5 & -2 \\\\ -3 & 1 \\end{pmatrix}$.',
        hindi: '$\\det = -1$, adj $= \\begin{pmatrix} 5 & -2 \\\\ -3 & 1 \\end{pmatrix}$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'System',
      problem: {
        english: 'For what value of $\\lambda$ does the system $x + y + z = 1,\\ x + 2y + 4z = \\lambda,\\ x + 4y + 10z = \\lambda^2$ have a solution?',
        hindi: '$\\lambda$ के किस मान के लिए सिस्टम $x + y + z = 1,\\ x + 2y + 4z = \\lambda,\\ x + 4y + 10z = \\lambda^2$ के पास कोई समाधान है?',
      },
      answer: {
        english: '$\\lambda = 1$ or $\\lambda = 2$',
        hindi: '$\\lambda = 1$ या $\\lambda = 2$',
      },
      solution: {
        english: 'Coefficient determinant $= 0$, so consistency requires $(\\text{adj}\\,A)B = O$, leading to $\\lambda^2 - 3\\lambda + 2 = 0$.',
        hindi: 'गुणांक निर्धारक $= 0$, इसलिए स्थिरता के लिए $(\\text{adj}\\,A)B = O$ की आवश्यकता होती है, जिससे $\\lambda^2 - 3\\lambda + 2 = 0$ होता है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Skew-symmetric',
      problem: {
        english: 'If $A$ is skew-symmetric of order 3, then $\\det(A) = ?$',
        hindi: 'यदि $A$ क्रम 3 का तिरछा-सममित है, तो $\\det(A) = ?$',
      },
      answer: {
        english: '$0$',
        hindi: '$0$',
      },
      solution: {
        english: 'For odd-order skew-symmetric, $\\det(A^T) = \\det(-A) = (-1)^n \\det(A) = -\\det(A)$, but $\\det(A^T) = \\det(A)$, so $\\det(A) = 0$.',
        hindi: 'विषम-क्रम तिरछा-सममिति के लिए, $\\det(A^T) = \\det(-A) = (-1)^n \\det(A) = -\\det(A)$, लेकिन $\\det(A^T) = \\det(A)$, इसलिए $\\det(A) = 0$।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Powers',
      problem: {
        english: 'If $A = \\begin{pmatrix} 1 & 1 \\\\ 0 & 1 \\end{pmatrix}$, find $A^n$.',
        hindi: 'यदि $A = \\begin{pmatrix} 1 & 1 \\\\ 0 & 1 \\end{pmatrix}$, तो $A^n$ खोजें।',
      },
      answer: {
        english: '$\\begin{pmatrix} 1 & n \\\\ 0 & 1 \\end{pmatrix}$',
        hindi: '$\\begin{pmatrix} 1 & n \\\\ 0 & 1 \\end{pmatrix}$',
      },
      solution: {
        english: 'Induction: $A^{n+1} = A^n \\cdot A$.',
        hindi: 'प्रेरण: $A^{n+1} = A^n \\cdot A$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Trace',
      problem: {
        english: 'If $A$ is $3 \\times 3$ with $\\text{tr}(A) = 3$ and $\\det(A) = 4$, find $\\text{tr}(\\text{adj}\\,A)$.',
        hindi: 'यदि $A$, $\\text{tr}(A) = 3$ और $\\det(A) = 4$ के साथ $3 \\times 3$ है, तो $\\text{tr}(\\text{adj}\\,A)$ खोजें।',
      },
      answer: {
        english: 'Sum of $2\\times2$ principal minors $= \\tfrac{1}{2}[(\\text{tr}\\,A)^2 - \\text{tr}(A^2)]$ — depends on $A^2$. (For specific $A$ given in PYQ, value follows.)',
        hindi: '$2\\times2$ प्रमुख अवयस्कों का योग $= \\tfrac{1}{2}[(\\text{tr}\\,A)^2 - \\text{tr}(A^2)]$ — $A^2$ पर निर्भर करता है। (PYQ में दिए गए विशिष्ट $A$ के लिए, मान निम्नानुसार है।)',
      },
      solution: {
        english: 'Use $\\text{tr}(\\text{adj}\\,A)$ = sum of $(n-1) \\times (n-1)$ cofactor diagonals = sum of principal $2\\times 2$ minors of $A$.',
        hindi: '$\\text{tr}(\\text{adj}\\,A)$ का उपयोग करें = $(n-1) \\times (n-1)$ का योग = सहकारक विकर्ण = मूलधन का योग $2\\times 2$ $A$ के अवयस्क।',
      },
    },
  ],
  next: {
    slug: 'determinants',
    title: 'Determinants',
  },
};

export default chapter;
